import { clamp } from 'lodash';
import { ReactNode } from 'react';
import { DEFAULT_CHART_WINDOW_STATE } from '../consts';
import { ChartWindowState } from '@ardoq/graph';
import { Point } from '@ardoq/math';
import { IconName } from '@ardoq/icons';
import {
  SettingsType,
  DropdownConfig,
  DisabledZoomControls,
  SettingsConfig,
} from '@ardoq/view-settings';
import { DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import {
  BubbleChartCommands,
  BubbleChartGridMode,
  BubbleChartQuadrantSettings,
  BubbleChartViewSettings,
  BubbleChartZoomControlsProps,
  Quadrant,
  QuadrantZoomState,
  SelectedQuadrantDropdownLabels,
  TimeLayoutLabels,
} from '../types';
import { getQuadrantSettings } from '../calculations';
import { zoomToQuadrant } from '../zoomToQuadrant';

export type { SelectedQuadrantDropdownLabels };

export type BubbleChartZoomControlsLabels = {
  zoomInLabel: string;
  zoomOutLabel: string;
  resetZoomLabel: string;
};

const DEFAULT_BUBBLE_CHART_ZOOM_CONTROLS_LABELS: BubbleChartZoomControlsLabels =
  {
    zoomInLabel: 'Zoom in',
    zoomOutLabel: 'Zoom out',
    resetZoomLabel: 'Reset zoom',
  };

const DEFAULT_SELECTED_QUADRANT_DROPDOWN_LABELS: SelectedQuadrantDropdownLabels =
  {
    noBackgroundSetUpLabel: 'No background set up',
    zoomToQuadrantLabel: 'Zoom to quadrant',
    showAllLabel: 'Show all',
    zoomToTemplate: quadrantLabel => `Zoom to ${quadrantLabel}`,
  };

export type GetBubbleChartZoomControlsArgs = ChartWindowState & {
  viewInstanceId: string;
  bubbleChartCommands: BubbleChartCommands;
  labels?: Partial<BubbleChartZoomControlsLabels>;
};

export const BUBBLE_CHART_SCALE_LIMITS = {
  min: 0.45,
  max: 1,
};

type AdjustBubbleChartZoomArgs = {
  increment: boolean;
  windowCenter: Point;
  windowScale: number;
  viewInstanceId: string;
  bubbleChartCommands: BubbleChartCommands;
};
const adjustBubbleChartZoom = ({
  increment,
  windowCenter,
  windowScale,
  viewInstanceId,
  bubbleChartCommands,
}: AdjustBubbleChartZoomArgs): void => {
  const step = windowScale * 0.2;

  const newWindowScale = clamp(
    windowScale + (increment ? -step : step),
    BUBBLE_CHART_SCALE_LIMITS.min,
    BUBBLE_CHART_SCALE_LIMITS.max
  );

  bubbleChartCommands.setScale(
    {
      windowCenter: newWindowScale === 1 ? [0.5, 0.5] : windowCenter,
      windowScale: newWindowScale,
    },
    viewInstanceId
  );
};
const bubbleChartZoom =
  (increment: boolean) =>
  ({
    windowCenter,
    windowScale,
    viewInstanceId,
    bubbleChartCommands,
  }: GetBubbleChartZoomControlsArgs) =>
  () =>
    adjustBubbleChartZoom({
      increment,
      windowCenter,
      windowScale,
      viewInstanceId,
      bubbleChartCommands,
    });

const getResetZoom =
  ({ viewInstanceId, bubbleChartCommands }: GetBubbleChartZoomControlsArgs) =>
  () =>
    bubbleChartCommands.setScale(DEFAULT_CHART_WINDOW_STATE, viewInstanceId);

const [getZoomIn, getChartZoomOut] = [true, false].map(increment =>
  bubbleChartZoom(increment)
);

export type BubbleChartZoomActions = {
  zoomIn: VoidFunction;
  zoomOut: VoidFunction;
  resetZoom: VoidFunction;
};

export const getBubbleChartZoomActions = (
  args: GetBubbleChartZoomControlsArgs
): BubbleChartZoomActions => ({
  zoomIn: getZoomIn(args),
  zoomOut: getChartZoomOut(args),
  resetZoom: getResetZoom(args),
});

const IconsByQuadrant: Record<Quadrant, IconName> = {
  [Quadrant.TOP_LEFT]: IconName.FOUR_SQUARE_TOP_LEFT,
  [Quadrant.TOP_RIGHT]: IconName.FOUR_SQUARE_TOP_RIGHT,
  [Quadrant.BOTTOM_LEFT]: IconName.FOUR_SQUARE_BOTTOM_LEFT,
  [Quadrant.BOTTOM_RIGHT]: IconName.FOUR_SQUARE_BOTTOM_RIGHT,
};
const isQuadrantSelectionDisabled = (
  viewSettings: BubbleChartViewSettings
): boolean =>
  viewSettings.background !== BubbleChartGridMode.CUSTOM &&
  viewSettings.background !== BubbleChartGridMode.TIME;

export const getSelectedQuadrantDropdown = (
  viewSettings: BubbleChartViewSettings,
  quadrantZoomState: QuadrantZoomState,
  viewInstanceId: string,
  windowState: ChartWindowState,
  disabledPopoverContent?: ReactNode,
  labelsProp?: Partial<SelectedQuadrantDropdownLabels>,
  timeLayoutLabels?: TimeLayoutLabels
) => {
  const labels: SelectedQuadrantDropdownLabels = {
    ...DEFAULT_SELECTED_QUADRANT_DROPDOWN_LABELS,
    ...labelsProp,
  };
  const { background, customQuadrantSettings } = viewSettings;

  const isDisabled = isQuadrantSelectionDisabled(viewSettings);

  if (isDisabled) {
    return {
      id: 'selectedQuadrant',
      label: labels.noBackgroundSetUpLabel,
      iconName: IconName.FOUR_SQUARE_GRID,
      options: [],
      type: SettingsType.DROPDOWN,
      isDisabled,
      popoverContent: disabledPopoverContent,
    } satisfies DropdownConfig;
  }

  const quadrantLabels = getQuadrantSettings(
    background,
    customQuadrantSettings,
    timeLayoutLabels
  )?.labels;

  const options = getQuadrantSelectorDropdownOptions(
    quadrantZoomState,
    windowState,
    viewInstanceId,
    labels,
    quadrantLabels
  );

  const iconName = quadrantZoomState.selectedQuadrant
    ? IconsByQuadrant[quadrantZoomState.selectedQuadrant]
    : IconName.FOUR_SQUARE_GRID;

  return {
    id: 'selectedQuadrant',
    label: labels.zoomToQuadrantLabel,
    iconName,
    options,
    type: SettingsType.DROPDOWN,
  } satisfies DropdownConfig;
};

const getQuadrantSelectorDropdownOptions = (
  quadrantZoomState: QuadrantZoomState,
  windowState: ChartWindowState,
  viewInstanceId: string,
  labels: SelectedQuadrantDropdownLabels,
  quadrantLabels: BubbleChartQuadrantSettings['labels'] = [
    'top left',
    'top right',
    'bottom left',
    'bottom right',
  ]
): DropdownOption[] => {
  const [topLeftLabel, topRightLabel, bottomLeftLabel, bottomRightLabel] =
    quadrantLabels;
  const { selectedQuadrant } = quadrantZoomState;
  return [
    {
      name: 'all',
      type: DropdownOptionType.OPTION,
      label: labels.showAllLabel,
      isActive: !selectedQuadrant,
      iconName: IconName.FOUR_SQUARE_GRID,
      onClick: () =>
        zoomToQuadrant({
          quadrant: null,
          currentWindowState: windowState,
          targetWindowState: DEFAULT_CHART_WINDOW_STATE,
          viewInstanceId,
        }),
    },
    {
      name: Quadrant.TOP_LEFT,
      type: DropdownOptionType.OPTION,
      label: labels.zoomToTemplate(topLeftLabel),
      isActive: selectedQuadrant === Quadrant.TOP_LEFT,
      iconName: IconsByQuadrant[Quadrant.TOP_LEFT],
      onClick: () =>
        zoomToQuadrant({
          quadrant: Quadrant.TOP_LEFT,
          currentWindowState: windowState,
          targetWindowState:
            quadrantZoomState?.quadrantWindowStates?.[Quadrant.TOP_LEFT] ||
            null,
          viewInstanceId,
        }),
    },
    {
      name: Quadrant.TOP_RIGHT,
      type: DropdownOptionType.OPTION,
      label: labels.zoomToTemplate(topRightLabel),
      isActive: selectedQuadrant === Quadrant.TOP_RIGHT,
      iconName: IconsByQuadrant[Quadrant.TOP_RIGHT],
      onClick: () =>
        zoomToQuadrant({
          quadrant: Quadrant.TOP_RIGHT,
          currentWindowState: windowState,
          targetWindowState:
            quadrantZoomState?.quadrantWindowStates?.[Quadrant.TOP_RIGHT] ||
            null,
          viewInstanceId,
        }),
    },
    {
      name: Quadrant.BOTTOM_LEFT,
      type: DropdownOptionType.OPTION,
      label: labels.zoomToTemplate(bottomLeftLabel),
      isActive: selectedQuadrant === Quadrant.BOTTOM_LEFT,
      iconName: IconsByQuadrant[Quadrant.BOTTOM_LEFT],
      onClick: () =>
        zoomToQuadrant({
          quadrant: Quadrant.BOTTOM_LEFT,
          currentWindowState: windowState,
          targetWindowState:
            quadrantZoomState?.quadrantWindowStates?.[Quadrant.BOTTOM_LEFT] ||
            null,
          viewInstanceId,
        }),
    },
    {
      name: Quadrant.BOTTOM_RIGHT,
      type: DropdownOptionType.OPTION,
      label: labels.zoomToTemplate(bottomRightLabel),
      isActive: selectedQuadrant === Quadrant.BOTTOM_RIGHT,
      iconName: IconsByQuadrant[Quadrant.BOTTOM_RIGHT],
      onClick: () =>
        zoomToQuadrant({
          quadrant: Quadrant.BOTTOM_RIGHT,
          currentWindowState: windowState,
          targetWindowState:
            quadrantZoomState?.quadrantWindowStates?.[Quadrant.BOTTOM_RIGHT] ||
            null,
          viewInstanceId,
        }),
    },
  ];
};

type GetZoomControlsPropsParams = {
  viewSettings: BubbleChartViewSettings;
  quadrantZoomState: QuadrantZoomState;
  viewInstanceId: string;
  windowState: ChartWindowState;
  bubbleChartCommands: BubbleChartCommands;
  hideQuadrantControls: boolean;
  selectedQuadrantDropdownLabels?: Partial<SelectedQuadrantDropdownLabels>;
  timeLayoutLabels?: TimeLayoutLabels;
};
export const getZoomControlsProps = ({
  viewSettings,
  quadrantZoomState,
  viewInstanceId,
  windowState,
  bubbleChartCommands,
  hideQuadrantControls,
  selectedQuadrantDropdownLabels,
  timeLayoutLabels,
}: GetZoomControlsPropsParams): BubbleChartZoomControlsProps => {
  const isDisabled = isQuadrantSelectionDisabled(viewSettings);
  const shouldShowQuadrantControls = !hideQuadrantControls && !isDisabled;
  const selectQuadrantButtonProps = shouldShowQuadrantControls
    ? getSelectedQuadrantDropdown(
        viewSettings,
        quadrantZoomState,
        viewInstanceId,
        windowState,
        undefined,
        selectedQuadrantDropdownLabels,
        timeLayoutLabels
      )
    : null;
  const getZoomControlsArgs = {
    windowCenter: windowState.windowCenter,
    windowScale: windowState.windowScale,
    viewInstanceId,
    bubbleChartCommands,
  };

  const isAbleToZoomIn =
    windowState.windowScale > BUBBLE_CHART_SCALE_LIMITS.min;
  const isAbleToZoomOut =
    windowState.windowScale < BUBBLE_CHART_SCALE_LIMITS.max;

  const disabledState =
    (!isAbleToZoomIn
      ? DisabledZoomControls.ZOOM_IN
      : DisabledZoomControls.NONE) |
    (!isAbleToZoomOut
      ? DisabledZoomControls.ZOOM_OUT
      : DisabledZoomControls.NONE);

  return {
    zoomIn: getZoomIn(getZoomControlsArgs),
    zoomOut: getChartZoomOut(getZoomControlsArgs),
    zoomCenter: getResetZoom(getZoomControlsArgs),
    disabledState,
    selectQuadrantButtonProps,
  };
};

export const getBubbleChartZoomControls = (
  args: GetBubbleChartZoomControlsArgs
): SettingsConfig[] => {
  const { windowScale, labels: labelsProp } = args;
  const labels: BubbleChartZoomControlsLabels = {
    ...DEFAULT_BUBBLE_CHART_ZOOM_CONTROLS_LABELS,
    ...labelsProp,
  };
  const { min: minLimit, max: maxLimit } = BUBBLE_CHART_SCALE_LIMITS;

  return [
    {
      id: 'zoomIn',
      label: labels.zoomInLabel,
      type: SettingsType.BUTTON,
      onClick: getZoomIn(args),
      iconName: IconName.ZOOM_IN,
      isDisabled: windowScale <= minLimit,
    },
    {
      id: 'zoomOut',
      label: labels.zoomOutLabel,
      type: SettingsType.BUTTON,
      onClick: getChartZoomOut(args),
      iconName: IconName.ZOOM_OUT,
      isDisabled: windowScale >= maxLimit,
    },
    {
      id: 'resetZoom',
      label: labels.resetZoomLabel,
      type: SettingsType.BUTTON,
      onClick: getResetZoom(args),
      iconName: IconName.ZOOM_OUT_MAP,
      isDisabled: windowScale === DEFAULT_CHART_WINDOW_STATE.windowScale,
    },
  ];
};
